import _ from "lodash";
import React from "react";
import UIModal from "UIComponents/UIModal";
import classes from "./SchedulePostModal.scss";
import moment from "moment";
import {
  IconButton,
  DatePicker,
  TimePicker,
  Button,
  Alert,
} from "@toddle-edu/ds-web";
import { CloseOutlined } from "@toddle-edu/ds-icons";
import classNames from "classnames";
import {
  getCombinedDateAndTimeMoment,
  isPortfolioParityEnabled,
} from "Post/utils";
import { useI18n } from "Hooks";

const PostScheduleModal = React.memo(props => {
  const {
    post = {},
    onClosePostScheduleModal,
    editDateDetails = {},
    onConfirmDateChange,
    canBackdate = true,
    dsVersion = "1.0",
    maxDate = undefined,
    maxTime = undefined,
    showOpenTimePickerDown = false,
  } = props;
  const { t } = useI18n(["journal", "common", "configuration"]);
  const [date, setDate] = React.useState(_.get(editDateDetails, "date"));
  const [time, setTime] = React.useState(_.get(editDateDetails, "time"));
  const isDsV2 = _.isEqual(dsVersion, "2.0");
  const handlePostDateChange = value => {
    setDate(value);
  };
  const handlePostTimeChange = value => {
    setTime(value);
  };

  const getStartTime = () => {
    const currentMoment = moment();
    if (!_.isEmpty(date) && date?.isSameOrBefore?.(currentMoment)) {
      currentMoment.subtract(15, "minutes");
      currentMoment.add(15 - (currentMoment.get("minutes") % 15), "minutes");
      return currentMoment;
    }
    return null;
  };

  const onDoneClick = () => {
    onConfirmDateChange?.({ date, time });
  };

  const isScheduled = getCombinedDateAndTimeMoment({ date, time })?.isAfter(
    moment()
  );
  const isBackdated = getCombinedDateAndTimeMoment({ date, time })?.isBefore(
    moment()
  );
  const modalContainer = classNames({
    [classes.schedulePostDateModalContainer]: !isPortfolioParityEnabled(),
    [classes.schedulePostDateModalContainerV2]: isPortfolioParityEnabled(),
    [classes.schedulePostDateModalContainerWithScheduleAlert]:
      isScheduled && !isPortfolioParityEnabled(),
    [classes.schedulePostDateModalContainerWithScheduleAlertV2]:
      isScheduled && isPortfolioParityEnabled() && !isDsV2,
    [classes.schedulePostDateModalContainerWithBackdateAlert]:
      isBackdated && !isPortfolioParityEnabled(),
    [classes.schedulePostDateModalContainerWithBackdateAlertV2]:
      isBackdated && isPortfolioParityEnabled() && !isDsV2,
    [classes.schedulePostDateModalContainerV3]: isDsV2,
    [classes.schedulePostDateModalContainerWithBackdateAlertV3]:
      isBackdated && isDsV2,
    [classes.schedulePostDateModalContainerWithScheduleAlertV3]:
      isScheduled && isDsV2,
  });

  const modalHeaderContainerClass = classNames({
    [classes.schedulePostDateModalHeaderV2]: isPortfolioParityEnabled(),
    [classes.schedulePostDateModalHeader]: !isPortfolioParityEnabled(),
  });

  const schedulePostDateModalFooterClass = classNames({
    [classes.schedulePostDateModalFooterV2]: isPortfolioParityEnabled,
    [classes.schedulePostDateModalFooter]: !isPortfolioParityEnabled(),
  });
  const getPopupContainer = () => document.body;
  return (
    <UIModal isOpen={true} modalContent={modalContainer}>
      <div
        className={classes.schedulePostDateModalInnerContainer}
        data-test-id="journal-postcreation-scheduling-modal"
      >
        <div className={modalHeaderContainerClass}>
          <div className="d-flex flex-column">
            <div className="heading-4">
              {" "}
              {t("journal:select_date_and_time")}{" "}
            </div>
          </div>
          <IconButton
            icon={<CloseOutlined />}
            variant="plain"
            onClick={onClosePostScheduleModal}
            dsVersion={dsVersion}
            type={"outlined"}
          />
        </div>
        <div className={classes.schedulePostDateModalBody}>
          <div className={classes.dateContainer}>
            <DatePicker
              label={t("common:select_date")}
              placeholder={t("common:select_date")}
              onChange={handlePostDateChange}
              picker="date"
              value={date}
              maxDate={maxDate}
              size={isDsV2 ? "medium" : "large"}
              variant="outlined"
              minDate={canBackdate ? undefined : moment()}
              testId="journal-postcreation-scheduling-datepicker"
              dsVersion={dsVersion}
            />
          </div>
          <div className={classes.timeContainer}>
            <TimePicker
              label={t("configuration:select_time")}
              value={time}
              onChange={handlePostTimeChange}
              format="HH:mm"
              size={isDsV2 ? "medium" : "large"}
              placement={"down"}
              minutesStep={15}
              startTime={canBackdate ? undefined : getStartTime()}
              testId="journal-postcreation-scheduling-timepicker"
              placeholder={"HH:MM"}
              dsVersion={dsVersion}
              maxTime={
                _.isEqual(moment(date)?.date(), moment(maxDate)?.date())
                  ? maxTime
                  : undefined
              }
              {...(showOpenTimePickerDown && { getPopupContainer })}
            />
          </div>
        </div>
        {isScheduled || isBackdated ? (
          <div className={classes.alertContainer}>
            <Alert
              variant="info"
              message={
                isScheduled
                  ? t("journal:alert_message_for_future_date")
                  : isBackdated
                  ? t("journal:alert_message_for_past_date")
                  : ""
              }
              testId="journal-postcreation-scheduling-alert"
              dsVersion={dsVersion}
            />
          </div>
        ) : null}
        <div className={schedulePostDateModalFooterClass}>
          <Button
            size={isDsV2 ? "medium" : "large"}
            variant={isDsV2 ? "neutral" : "outlined-subtle"}
            type={"outlined"}
            style={{ marginRight: "12px" }}
            onClick={onClosePostScheduleModal}
            testId="journal-postcreation-scheduling-cancel"
            dsVersion={dsVersion}
          >
            {t("common:cancel")}
          </Button>
          <Button
            size={isDsV2 ? "medium" : "large"}
            variant={isDsV2 ? "primary" : "progressive"}
            type={"fill"}
            dsVersion={dsVersion}
            onClick={onDoneClick}
            disabled={_.isEmpty(date) || _.isEmpty(time)}
            testId="journal-postcreation-scheduling-done"
          >
            {t("common:done")}
          </Button>
        </div>
      </div>
    </UIModal>
  );
});

PostScheduleModal.displayName = "PostScheduleModal";

export default PostScheduleModal;
